// src/components/pages/Legal.jsx
//
// Three legal/policy pages, exported separately and routed independently:
//   - Terms of Use         → /terms      (export TermsOfUse)
//   - Privacy Policy       → /privacy    (export PrivacyPolicy)
//   - Refund Policy        → /refund     (export RefundPolicy)
//
// Content priorities:
//   1. Make crystal clear this is an ENTERTAINMENT product.
//   2. Make crystal clear what LottoGPT does NOT do
//      (sell tickets, collect winnings, manage purchases, guarantee outcomes).
//   3. Limit liability for user decisions to play the lottery.
//   4. No claims of guaranteed wins, increased odds, or profitability.
//   5. Cover payments, refunds, IP, governing law in plain legalese.
//
// NOT a substitute for review by a licensed attorney. We strongly advise
// having a US-based attorney (familiar with state gambling/sweepstakes
// law) review before launch in any new jurisdiction.

import React from "react";
import AppShell from "../layout/AppShell";
import Card from "../ui/Card";

const EFFECTIVE_DATE = "May 15, 2026"; // ← Update before each major launch.
const COMPANY = "LottoGPT";
const CONTACT_EMAIL = "support@lotto-gpt.io";

/* ============================================================
   Shared layout
   ============================================================ */
function LegalShell({ title, lastUpdated, children }) {
  return (
    <AppShell>
      <div className="px-4 sm:px-6 py-10 max-w-[820px] mx-auto">
        <header className="mb-8">
          <div className="lg-label mb-2">Legal</div>
          <h1
            className="font-semibold mb-2"
            style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)", color: "var(--txt)" }}
          >
            {title}
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-3)" }}>
            Last updated: {lastUpdated}
          </p>
        </header>

        <Card>
          <article className="legal-prose">{children}</article>
        </Card>
      </div>

      {/* Inline page-scoped typography for the article — kept here so the
          page is self-contained and doesn't require app-wide CSS edits. */}
      <style>{`
        .legal-prose h2 {
          font-size: 18px;
          font-weight: 600;
          color: var(--txt);
          margin-top: 28px;
          margin-bottom: 10px;
        }
        .legal-prose h2:first-child { margin-top: 0; }
        .legal-prose h3 {
          font-size: 14px;
          font-weight: 600;
          color: var(--txt);
          margin-top: 18px;
          margin-bottom: 6px;
        }
        .legal-prose p, .legal-prose li {
          font-size: 13.5px;
          line-height: 1.65;
          color: var(--txt-2);
          margin-bottom: 10px;
        }
        .legal-prose ul, .legal-prose ol { padding-left: 22px; margin-bottom: 12px; }
        .legal-prose ul { list-style: disc; }
        .legal-prose ol { list-style: decimal; }
        .legal-prose strong { color: var(--txt); font-weight: 600; }
        .legal-prose a { color: var(--gold); text-decoration: none; }
        .legal-prose a:hover { text-decoration: underline; }
        .legal-prose .callout {
          background: var(--gold-dim);
          border: 1px solid var(--gold-border);
          border-radius: var(--r-lg);
          padding: 14px 16px;
          margin: 18px 0;
        }
        .legal-prose .callout p { color: var(--txt); margin-bottom: 8px; }
        .legal-prose .callout p:last-child { margin-bottom: 0; }
        .legal-prose .callout ul { margin-bottom: 0; }
        .legal-prose .callout ul li { color: var(--txt); margin-bottom: 4px; }
        .legal-prose .callout ul li:last-child { margin-bottom: 0; }
      `}</style>
    </AppShell>
  );
}

/* ============================================================
   TERMS OF USE
   ============================================================ */
export function TermsOfUse() {
  return (
    <LegalShell title="Terms of Use" lastUpdated={EFFECTIVE_DATE}>
      <div className="callout">
        <p>
          <strong>{COMPANY} is an entertainment product.</strong> Lottery
          drawings are random. Number combinations generated by this
          Service are <strong>for entertainment purposes only</strong> and
          do not guarantee any winning outcome. By using the Service, you
          acknowledge that any decision to purchase a lottery ticket, and
          the financial consequences thereof, are your own.
        </p>
      </div>

      <div className="callout">
        <p><strong>What {COMPANY} does not do.</strong> For clarity:</p>
        <ul>
          <li>{COMPANY} <strong>does not sell lottery tickets.</strong></li>
          <li>{COMPANY} <strong>does not collect, claim, or hold lottery winnings</strong> on your behalf.</li>
          <li>{COMPANY} <strong>does not manage or facilitate official lottery purchases</strong> in any way.</li>
          <li>Payments made on {COMPANY} are <strong>only for prepaid credits</strong> used to access the number-generation service. They are <strong>not</strong> payments for lottery tickets or entries.</li>
          <li>You are <strong>solely responsible</strong> for purchasing official lottery tickets through authorized retailers in your jurisdiction.</li>
          <li>{COMPANY} <strong>does not guarantee any winnings, prize, or profitable outcome.</strong></li>
        </ul>
      </div>

      <h2>1. Acceptance of Terms</h2>
      <p>
        These Terms of Use (the "Terms") form a legally binding agreement
        between you ("you", "your", or "User") and {COMPANY} ("we", "us",
        or "our") governing your access to and use of the website,
        applications, and services made available by {COMPANY} (collectively,
        the "Service"). By creating an account, accessing the Service, or
        purchasing credits, you agree to be bound by these Terms. If you do
        not agree, do not use the Service.
      </p>

      <h2>2. Eligibility</h2>
      <p>You represent and warrant that:</p>
      <ul>
        <li>You are at least <strong>eighteen (18) years of age</strong>,
            or the age of majority in your jurisdiction, whichever is older;</li>
        <li>You reside in a U.S. state or territory in which participation
            in state-licensed lottery games is legal;</li>
        <li>You have the legal capacity to enter into a binding contract;</li>
        <li>Your use of the Service does not violate any applicable law,
            regulation, or order in your jurisdiction.</li>
      </ul>
      <p>
        We reserve the right to suspend, restrict, or terminate accounts
        we reasonably believe to be in violation of this section, without
        notice and without liability.
      </p>

      <h2>3. Nature of the Service — Entertainment Only</h2>
      <p>
        The Service generates suggested lottery number combinations using
        statistical models trained on publicly available historical draw
        data. The Service is provided <strong>as a form of entertainment</strong>{" "}
        and is not, and shall not be construed as:
      </p>
      <ul>
        <li>A prediction of winning lottery numbers;</li>
        <li>Investment, financial, gambling, or any other professional advice;</li>
        <li>A representation that any specific outcome will occur;</li>
        <li>A system, strategy, or guarantee that improves your odds of
            winning any lottery.</li>
      </ul>
      <p>
        <strong>Lottery games are games of chance.</strong> The probability of
        winning is determined solely by the rules of the applicable lottery
        operator and the random nature of the draw, and is not influenced
        by use of the Service.
      </p>

      <h2>4. No Affiliation with Lottery Operators; No Ticket Sales</h2>
      <p>
        {COMPANY} is not affiliated with, endorsed by, or sponsored by
        Mega Millions, Powerball, the Multi-State Lottery Association, any
        state lottery commission, or any other lottery operator. All trademarks,
        service marks, and trade names belong to their respective owners.
      </p>
      <p>
        <strong>{COMPANY} does not sell, broker, resell, or facilitate the
        purchase of lottery tickets, and does not collect or manage lottery
        winnings.</strong> The Service only generates suggested number
        combinations for entertainment. To play, you must purchase an
        official ticket yourself, in person or through an officially
        authorized channel, in a jurisdiction where doing so is legal.
        Any prize associated with an official ticket is claimed by the
        ticket holder directly from the relevant lottery operator;
        {" "}{COMPANY} has no role in that process.
      </p>

      <h2>5. User Accounts</h2>
      <p>
        You are responsible for maintaining the confidentiality of your
        account credentials and for all activity occurring under your
        account. You must notify us immediately at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> of any
        unauthorized use. We are not liable for any loss arising from
        unauthorized account access where you failed to maintain reasonable
        credential security.
      </p>

      <h2>6. Credits, Purchases, and Subscriptions</h2>
      <p>
        Access to certain features requires the purchase of credits or a
        recurring subscription. <strong>All payments made to {COMPANY} are
        solely for prepaid credits or subscriptions that grant access to
        the number-generation service.</strong> Payments are <strong>not</strong>{" "}
        purchases of lottery tickets, entries, or chances to win, and
        {" "}{COMPANY} provides no lottery ticket of any kind in exchange.
      </p>
      <p>
        Credits are non-transferable, have no cash value outside the
        Service, and are governed by our{" "}
        <a href="/refund">Refund Policy</a>. Subscriptions renew automatically
        until canceled by you. You authorize us, through our payment processors
        (Stripe and/or PayPal), to charge your selected payment method on
        each renewal cycle until you cancel.
      </p>

      <h2>7. Acceptable Use</h2>
      <p>You agree NOT to:</p>
      <ul>
        <li>Use the Service for any illegal purpose, including any form
            of gambling prohibited in your jurisdiction;</li>
        <li>Reverse-engineer, decompile, scrape, or otherwise attempt to
            extract source code, models, or proprietary algorithms;</li>
        <li>Access the Service via automated means (bots, scrapers,
            crawlers) without our prior written consent;</li>
        <li>Resell, redistribute, or commercialize generated number
            combinations as a separate paid service;</li>
        <li>Misrepresent the Service's output as guaranteed winning
            numbers to any third party;</li>
        <li>Upload malicious code or attempt to interfere with the
            integrity of the Service.</li>
      </ul>

      <h2>8. Intellectual Property</h2>
      <p>
        All software, designs, text, graphics, logos, models, and other
        materials comprising the Service are owned by {COMPANY} or its
        licensors and are protected by U.S. and international copyright,
        trademark, and other intellectual property laws. We grant you a
        limited, non-exclusive, non-transferable, revocable license to use
        the Service solely for your personal, non-commercial use, subject
        to these Terms.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED <strong>"AS IS"</strong> AND{" "}
        <strong>"AS AVAILABLE"</strong> WITHOUT WARRANTIES OF ANY KIND,
        WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
        IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTY ARISING FROM COURSE
        OF DEALING OR USAGE OF TRADE.
      </p>
      <p>
        Without limiting the foregoing, we make no representation or
        warranty that the Service: (a) will be uninterrupted, secure, or
        error-free; (b) will produce any particular result; (c) will result
        in any winning lottery outcome; or (d) is suitable for your
        particular needs.
      </p>

      <h2>10. Limitation of Liability — User Decisions to Play</h2>
      <p>
        <strong>You expressly acknowledge and agree that your decision to
        purchase a lottery ticket, the amount you spend on tickets, and
        any financial loss arising therefrom, are your own personal
        decisions.</strong> {COMPANY} is not responsible for any such
        decisions or losses, regardless of whether you made them in reliance
        on output from the Service.
      </p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
        SHALL {COMPANY.toUpperCase()}, ITS OFFICERS, DIRECTORS, EMPLOYEES,
        AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
        LIMITED TO LOSS OF PROFITS, LOSS OF DATA, LOSS OF GOODWILL,
        GAMBLING LOSSES, OR ANY OTHER INTANGIBLE LOSSES, ARISING OUT OF OR
        RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF
        ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p>
        Our aggregate liability for any claims arising out of or relating
        to the Service shall not exceed the greater of (i) the total
        amount you paid to us in the twelve (12) months immediately
        preceding the event giving rise to the claim, or (ii) one hundred
        U.S. dollars (USD $100).
      </p>

      <h2>11. Responsible Play</h2>
      <p>
        We encourage responsible play. Only spend on lottery tickets what
        you can afford to lose. If you or someone you know may have a
        gambling problem, help is available 24/7 from the{" "}
        <a href="https://www.ncpgambling.org/help-treatment/" target="_blank" rel="noopener noreferrer">
          National Council on Problem Gambling
        </a>{" "}
        or by calling 1-800-GAMBLER.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless {COMPANY} and
        its affiliates, officers, employees, and agents from and against
        any and all claims, liabilities, damages, losses, costs, and
        expenses (including reasonable attorneys' fees) arising out of or
        related to (a) your use of the Service; (b) your violation of these
        Terms; or (c) your violation of any rights of a third party.
      </p>

      <h2>13. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at any
        time, with or without notice, for any reason, including but not
        limited to a breach of these Terms. Upon termination, your right
        to use the Service ceases immediately. Sections 8 (IP),
        9 (Disclaimers), 10 (Limitation of Liability), 12 (Indemnification),
        and 15 (Governing Law) survive termination.
      </p>

      <h2>14. Modifications</h2>
      <p>
        We may modify these Terms at any time by posting a revised version
        in the Service. Continued use of the Service after the effective
        date of any revision constitutes acceptance of the revised Terms.
      </p>

      <h2>15. Governing Law &amp; Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of the State of Florida,
        without regard to its conflict-of-laws principles. Any dispute
        arising out of or relating to these Terms or the Service shall be
        resolved by binding individual arbitration administered by the
        American Arbitration Association under its Consumer Arbitration
        Rules. <strong>You waive any right to participate in a class
        action or class-wide arbitration.</strong> Notwithstanding the
        foregoing, either party may seek injunctive or equitable relief in
        a court of competent jurisdiction for matters relating to
        intellectual property.
      </p>

      <h2>16. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

/* ============================================================
   PRIVACY POLICY
   ============================================================ */
export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" lastUpdated={EFFECTIVE_DATE}>
      <p>
        This Privacy Policy describes how {COMPANY} ("we", "us", "our")
        collects, uses, and shares information about you when you use our
        website, applications, and services (the "Service"). By using the
        Service, you consent to the practices described below.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Information you provide</h3>
      <ul>
        <li><strong>Account information:</strong> name, email address, and
            password when you register;</li>
        <li><strong>Payment information:</strong> billing details processed
            by our payment processors (Stripe, PayPal). We do not store
            full card numbers;</li>
        <li><strong>Communications:</strong> messages you send via our
            contact form or support email.</li>
      </ul>

      <h3>Information collected automatically</h3>
      <ul>
        <li>Device and browser information (IP address, user agent,
            language, time zone);</li>
        <li>Usage information (pages viewed, features used, time spent,
            number generations performed);</li>
        <li>Authentication cookies and a CSRF cookie, which are strictly
            necessary for the Service to function.</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <p>We use information to:</p>
      <ul>
        <li>Provide, operate, and improve the Service;</li>
        <li>Authenticate users and secure accounts;</li>
        <li>Process payments and manage subscriptions;</li>
        <li>Respond to support requests;</li>
        <li>Detect, prevent, and address fraud or abuse;</li>
        <li>Comply with legal obligations.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell your personal information. We do
        not use your information to make any representation about whether
        you will win a lottery.
      </p>

      <h2>3. How We Share Information</h2>
      <p>We share information only with:</p>
      <ul>
        <li><strong>Service providers</strong> acting on our behalf (e.g.,
            hosting, email delivery, payment processing) under written
            agreements that restrict their use of your data;</li>
        <li><strong>Law enforcement or regulators</strong> when required
            by law, subpoena, or to protect our legal rights;</li>
        <li><strong>Acquirers</strong> in the event of a merger,
            acquisition, or sale of assets — your information would be
            transferred subject to substantially similar privacy
            protections.</li>
      </ul>

      <h2>4. Cookies</h2>
      <p>
        We use only cookies that are strictly necessary to operate the
        Service: authentication (HttpOnly session cookie) and a CSRF token.
        We do not use third-party advertising cookies. We do not engage in
        cross-site behavioral advertising.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain account and usage data for as long as your account is
        active and for a reasonable period thereafter to comply with legal
        obligations, resolve disputes, and enforce agreements. Generated
        number history is retained while your account remains active.
        You may request account deletion at any time (see Section 7).
      </p>

      <h2>6. Security</h2>
      <p>
        We use industry-standard administrative, technical, and physical
        safeguards to protect personal information, including encryption
        in transit (TLS), encrypted credential storage, and access controls.
        No method of transmission or storage is 100% secure, however, and
        we cannot guarantee absolute security.
      </p>

      <h2>7. Your Rights</h2>
      <p>
        Depending on your jurisdiction (including residents of California
        under the CCPA/CPRA, and Virginia, Colorado, Connecticut, and Utah
        under their respective state privacy laws), you may have the right
        to:
      </p>
      <ul>
        <li>Know what personal information we hold about you;</li>
        <li>Request access to or a copy of that information;</li>
        <li>Request correction of inaccurate information;</li>
        <li>Request deletion of your information;</li>
        <li>Opt out of certain processing (we do not sell or share for
            cross-context behavioral advertising regardless);</li>
        <li>Not be subject to discrimination for exercising these rights.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will
        respond within the time required by applicable law.
      </p>

      <h2>8. Children's Privacy</h2>
      <p>
        The Service is not directed to individuals under 18. We do not
        knowingly collect personal information from anyone under 18. If
        we learn that we have collected information from a person under 18,
        we will delete it promptly.
      </p>

      <h2>9. International Users</h2>
      <p>
        The Service is hosted in the United States. If you access the
        Service from outside the U.S., your information will be transferred
        to and processed in the U.S., which may have data-protection laws
        different from those in your country.
      </p>

      <h2>10. Changes to this Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post
        the revised version with an updated "Last updated" date. Continued
        use of the Service after the effective date constitutes acceptance
        of the revised Policy.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions or requests? Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

/* ============================================================
   REFUND POLICY
   ============================================================ */
export function RefundPolicy() {
  return (
    <LegalShell title="Refund Policy" lastUpdated={EFFECTIVE_DATE}>
      <div className="callout">
        <p>
          <strong>Important:</strong> All payments made to {COMPANY} are
          solely for <strong>prepaid credits or subscriptions</strong> that
          grant access to the number-generation service. They are <strong>not</strong>{" "}
          payments for lottery tickets, entries, or chances to win.
        </p>
        <p>
          Credits are consumed at the moment they generate number
          combinations. Once credits have been used, they are
          non-refundable. This policy does not affect your statutory rights
          where applicable law provides otherwise.
        </p>
      </div>

      <h2>1. Credit Purchases (One-Time)</h2>
      <p>
        One-time credit purchases (Single, Basic, Gold, Platinum packages)
        may be refunded in full <strong>only if</strong>:
      </p>
      <ul>
        <li>The purchase was made within the past <strong>seven (7) days</strong>;</li>
        <li><strong>None of the purchased credits have been used</strong>; and</li>
        <li>You contact{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with
            your account email and order reference.</li>
      </ul>
      <p>
        Refunds are issued to the original payment method and may take
        5–10 business days to appear on your statement.
      </p>

      <h2>2. Monthly Subscriptions</h2>
      <p>
        Monthly subscriptions renew automatically. You may cancel a
        subscription at any time, and cancellation prevents future
        renewals. <strong>Charges already incurred are non-refundable</strong>,
        except in the following cases:
      </p>
      <ul>
        <li>An accidental duplicate charge — refunded in full upon
            verification;</li>
        <li>A failed renewal where the subscription was already
            canceled — refunded in full;</li>
        <li>Service outage exceeding 72 consecutive hours during a
            billing cycle — a prorated credit may be issued at our
            discretion.</li>
      </ul>

      <h2>3. Credits Already Used</h2>
      <p>
        Credits consumed to generate number combinations are{" "}
        <strong>non-refundable under all circumstances</strong>. This
        includes (without limitation) cases where you did not win a
        lottery prize, where the official lottery ticket you purchased
        separately did not win, or where you are dissatisfied with the
        generated numbers.
      </p>

      <h2>4. No Refunds for Lottery Outcomes</h2>
      <p>
        Because {COMPANY} does not sell lottery tickets or collect
        winnings, any outcome of an official lottery drawing &mdash; whether
        the ticket you purchased independently won or lost &mdash; is unrelated
        to your purchase of credits from {COMPANY} and is <strong>not
        grounds for a refund</strong>.
      </p>

      <h2>5. Chargebacks</h2>
      <p>
        We encourage you to contact us before initiating a chargeback.
        Disputes are typically resolved more quickly through direct
        support. Accounts that initiate chargebacks for legitimate
        charges may be suspended or terminated.
      </p>

      <h2>6. Statutory Rights</h2>
      <p>
        Nothing in this policy limits any non-waivable rights you may
        have under applicable consumer protection law in your jurisdiction.
        Where mandatory statutory refund rights apply, this policy is
        construed to be consistent with those rights.
      </p>

      <h2>7. How to Request a Refund</h2>
      <p>
        Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with:
      </p>
      <ul>
        <li>The email address associated with your account;</li>
        <li>The approximate date of the purchase;</li>
        <li>The order or transaction reference, if available;</li>
        <li>A brief description of the reason for the request.</li>
      </ul>
      <p>
        We aim to respond within two (2) business days.
      </p>
    </LegalShell>
  );
}
